import { useState } from "react";
import { MessageCircle, FileText, Eye, CheckCircle2 } from "lucide-react";
import InquiryDialog from "@/components/InquiryDialog";
import ProductDetailModal from "@/components/ProductDetailModal";
import ProductImage from "@/components/ProductImage";
import { waLink } from "@/lib/constants";

export default function ProductCard({ product }) {
    const [inquireOpen, setInquireOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);

    const inStock = (product.availability || "").toLowerCase().includes("in stock");

    return (
        <div
            data-testid={`product-card-${product.id}`}
            className="group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-brand/40 hover:shadow-[0_16px_40px_rgba(10,37,64,0.08)] hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300"
        >
            <button
                type="button"
                onClick={() => setDetailOpen(true)}
                data-testid={`product-image-btn-${product.id}`}
                className="relative aspect-[4/3] block overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label={`View details for ${product.name}`}
            >
                <ProductImage product={product} />
            </button>

            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-brand">
                        {product.brand}
                    </span>
                    <span
                        data-testid={`product-availability-${product.id}`}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            inStock
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                    >
                        <CheckCircle2 className="w-3 h-3" />
                        {product.availability || "Available"}
                    </span>
                </div>
                <h3 className="mt-1.5 font-display text-base font-semibold text-navy leading-snug line-clamp-2">
                    {product.name}
                </h3>
                <div className="mt-1 text-[11px] font-mono text-slate-500" data-testid={`product-model-${product.id}`}>
                    Model: {product.model_number}
                </div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">
                    {product.short_description}
                </p>
                <ul className="mt-3 space-y-1">
                    {(product.specs || []).slice(0, 3).map((s) => (
                        <li key={s} className="flex items-start gap-2 text-xs text-slate-600">
                            <span className="w-1 h-1 rounded-full bg-brand mt-1.5 shrink-0" />
                            <span className="line-clamp-1">{s}</span>
                        </li>
                    ))}
                </ul>
                <div className="mt-5 grid grid-cols-3 gap-1.5">
                    <button
                        onClick={() => setDetailOpen(true)}
                        data-testid={`product-details-btn-${product.id}`}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 text-navy px-2 py-2 text-xs font-semibold transition-colors"
                    >
                        <Eye className="w-3.5 h-3.5" /> Details
                    </button>
                    <button
                        onClick={() => setInquireOpen(true)}
                        data-testid={`product-inquiry-btn-${product.id}`}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-brand hover:bg-brand-hover text-white px-2 py-2 text-xs font-semibold transition-colors"
                    >
                        <FileText className="w-3.5 h-3.5" /> Quote
                    </button>
                    <a
                        href={waLink(`Hi CoreCyrus, please quote for ${product.name} (${product.brand} ${product.model_number}).`)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`product-whatsapp-btn-${product.id}`}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white px-2 py-2 text-xs font-semibold transition-colors"
                    >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                </div>
            </div>

            <InquiryDialog open={inquireOpen} onClose={() => setInquireOpen(false)} product={product} />
            <ProductDetailModal
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                product={product}
                onRequestQuote={() => {
                    setDetailOpen(false);
                    setInquireOpen(true);
                }}
            />
        </div>
    );
}
